import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { COUNTRY_FACTS, COUNTRY_SLUGS, getCountryContent } from '../data/countriesContent';
import { ArrowRight, ChevronDown } from 'lucide-react';

/**
 * Side-by-side regulatory comparator between the current country and any
 * other EASA state. Purpose:
 *   1. Deep SEO internal linking — every country page links to 31 others
 *      via the auto-populated peer card (32 × 31 = 992 combinations).
 *   2. Long-tail queries like "réglementation drone Espagne vs France"
 *      or "drone rules Germany vs Austria" land here.
 *   3. UX: helps operators flying across borders spot the differences
 *      in authority, mandatory portal and altitude cap in one glance.
 *
 * All data pulled from COUNTRY_FACTS (authority, portal, coordinates)
 * and getCountryContent() so the comparator stays in sync with the rest
 * of the country pages — no separate schema to keep updated.
 */

const LABELS = {
  fr: {
    title: 'Comparer avec un autre pays',
    subtitle: 'Voyez les différences entre ce pays et un autre État EASA — pour préparer vos missions transfrontalières.',
    pick: 'Choisir un pays',
    authority: 'Autorité de tutelle',
    portal: "Plateforme d'enregistrement",
    notam: 'Source NOTAM / trafic',
    ceiling: 'Plafond altitude par défaut',
    ceilingValue: '120 m AGL (EU 2019/947)',
    registration: 'Enregistrement obligatoire',
    registrationValue: 'Dès 250 g ou avec caméra',
    training: 'Formation A1/A3',
    trainingValue: 'Requise (test en ligne EASA)',
    seeFull: 'Voir la fiche complète',
    keyDiff: 'Différences clés',
    sameFramework: 'Cadre EU 2019/947 identique',
    sameFrameworkDesc: 'Les deux pays appliquent le règlement européen. Catégories Open (A1/A2/A3), Specific et Certified reconnues mutuellement (mutuelle EASA).',
  },
  en: {
    title: 'Compare with another country',
    subtitle: 'See the differences between this country and another EASA state — plan your cross-border missions.',
    pick: 'Pick a country',
    authority: 'Regulatory authority',
    portal: 'Registration portal',
    notam: 'NOTAM / ATC source',
    ceiling: 'Default altitude ceiling',
    ceilingValue: '120 m AGL (EU 2019/947)',
    registration: 'Mandatory registration',
    registrationValue: 'From 250 g or with a camera',
    training: 'A1/A3 training',
    trainingValue: 'Required (EASA online test)',
    seeFull: 'See full guide',
    keyDiff: 'Key differences',
    sameFramework: 'Same EU 2019/947 framework',
    sameFrameworkDesc: 'Both countries apply the European regulation. Open (A1/A2/A3), Specific and Certified categories mutually recognised (EASA mutual recognition).',
  },
  de: {
    title: 'Mit einem anderen Land vergleichen',
    subtitle: 'Sehen Sie die Unterschiede zwischen diesem und einem anderen EASA-Staat — planen Sie grenzüberschreitende Einsätze.',
    pick: 'Land wählen',
    authority: 'Aufsichtsbehörde',
    portal: 'Registrierungsportal',
    notam: 'NOTAM- / ATC-Quelle',
    ceiling: 'Standard-Flughöhe',
    ceilingValue: '120 m AGL (EU 2019/947)',
    registration: 'Registrierungspflicht',
    registrationValue: 'Ab 250 g oder mit Kamera',
    training: 'A1/A3-Ausbildung',
    trainingValue: 'Erforderlich (EASA Online-Test)',
    seeFull: 'Vollständiger Leitfaden',
    keyDiff: 'Hauptunterschiede',
    sameFramework: 'Gleicher EU 2019/947-Rahmen',
    sameFrameworkDesc: 'Beide Länder wenden die europäische Verordnung an. Open (A1/A2/A3), Specific und Certified werden gegenseitig anerkannt (EASA).',
  },
  it: {
    title: 'Confronta con un altro paese',
    subtitle: 'Guarda le differenze tra questo paese e un altro Stato EASA — per pianificare missioni transfrontaliere.',
    pick: 'Scegli un paese',
    authority: 'Autorità di regolamentazione',
    portal: 'Portale di registrazione',
    notam: 'Fonte NOTAM / ATC',
    ceiling: 'Altitudine massima predefinita',
    ceilingValue: '120 m AGL (EU 2019/947)',
    registration: 'Registrazione obbligatoria',
    registrationValue: "Da 250 g o con fotocamera",
    training: 'Formazione A1/A3',
    trainingValue: 'Richiesta (test online EASA)',
    seeFull: 'Guida completa',
    keyDiff: 'Differenze principali',
    sameFramework: 'Stesso quadro EU 2019/947',
    sameFrameworkDesc: 'Entrambi i paesi applicano il regolamento europeo. Open (A1/A2/A3), Specific e Certified riconosciute reciprocamente (EASA).',
  },
  es: {
    title: 'Comparar con otro país',
    subtitle: 'Descubre las diferencias entre este país y otro Estado EASA — para preparar misiones transfronterizas.',
    pick: 'Elige un país',
    authority: 'Autoridad reguladora',
    portal: 'Portal de registro',
    notam: 'Fuente NOTAM / ATC',
    ceiling: 'Altitud máxima predeterminada',
    ceilingValue: '120 m AGL (EU 2019/947)',
    registration: 'Registro obligatorio',
    registrationValue: 'Desde 250 g o con cámara',
    training: 'Formación A1/A3',
    trainingValue: 'Obligatoria (test online EASA)',
    seeFull: 'Ver guía completa',
    keyDiff: 'Diferencias clave',
    sameFramework: 'Mismo marco EU 2019/947',
    sameFrameworkDesc: 'Ambos países aplican el reglamento europeo. Open (A1/A2/A3), Specific y Certified mutuamente reconocidas (EASA).',
  },
};

export default function CountryComparator({ currentSlug, lang = 'fr', standalone = false }) {
  const t = LABELS[lang] || LABELS.fr;
  // On country pages, the left side is fixed to the current country.
  // On the landing page (`standalone`), both sides are pickable so
  // visitors can build any 32×31 comparison without navigating first.
  const [leftSlug, setLeftSlug] = useState(currentSlug || 'france');
  const [peerSlug, setPeerSlug] = useState(
    (currentSlug === 'france' || (standalone && !currentSlug)) ? 'germany' : 'france',
  );
  const activeLeft = standalone ? leftSlug : currentSlug;

  const peers = useMemo(
    () => COUNTRY_SLUGS.filter((s) => s !== activeLeft).sort(),
    [activeLeft],
  );
  const leftOptions = useMemo(
    () => COUNTRY_SLUGS.filter((s) => s !== peerSlug).sort(),
    [peerSlug],
  );

  const current = useMemo(
    () => ({ facts: COUNTRY_FACTS[activeLeft], content: getCountryContent(activeLeft, lang) }),
    [activeLeft, lang],
  );
  const peer = useMemo(
    () => ({ facts: COUNTRY_FACTS[peerSlug], content: getCountryContent(peerSlug, lang) }),
    [peerSlug, lang],
  );

  if (!current.facts || !peer.facts) return null;

  // URL prefix for the peer's country page — respects language routing so
  // Spanish speakers stay on Spanish links, etc.
  const peerHref = lang === 'fr' ? `/regulations/${peerSlug}` : `/${lang}/regulations/${peerSlug}`;
  const currentHref = lang === 'fr' ? `/regulations/${activeLeft}` : `/${lang}/regulations/${activeLeft}`;

  const rows = [
    { label: t.authority,    a: current.facts.authorityFull || current.facts.authority, b: peer.facts.authorityFull || peer.facts.authority },
    { label: t.portal,       a: current.facts.registration,  b: peer.facts.registration },
    { label: t.notam,        a: current.facts.notamSource,   b: peer.facts.notamSource },
    { label: t.ceiling,      a: t.ceilingValue,              b: t.ceilingValue },
    { label: t.registration, a: t.registrationValue,         b: t.registrationValue },
    { label: t.training,     a: t.trainingValue,             b: t.trainingValue },
  ];

  return (
    <section className="py-16 bg-slate-50 border-t border-slate-200" data-testid="country-comparator">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900">
            {t.title}
          </h2>
          <p className="text-slate-600 mt-2 max-w-3xl">{t.subtitle}</p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
          <div className="flex-1 w-full">
            {standalone ? (
              <>
                <label className="block text-xs font-mono text-slate-500 uppercase mb-1">{t.pick}</label>
                <div className="relative">
                  <select
                    value={leftSlug}
                    onChange={(e) => setLeftSlug(e.target.value)}
                    className="w-full appearance-none bg-white border border-slate-300 rounded-lg px-3 py-2 pr-9 font-semibold text-slate-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none cursor-pointer"
                    data-testid="comparator-left-select"
                  >
                    {leftOptions.map((s) => {
                      const c = getCountryContent(s, lang);
                      return <option key={s} value={s}>{c?.name || s}</option>;
                    })}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </>
            ) : (
              <>
                <div className="text-xs font-mono text-slate-500 uppercase mb-1">{current.facts.iso}</div>
                <div className="font-bold text-lg text-slate-900">{current.content?.name || activeLeft}</div>
              </>
            )}
          </div>
          <ArrowRight className="text-slate-400 flex-shrink-0" />
          <div className="flex-1 w-full">
            <label className="block text-xs font-mono text-slate-500 uppercase mb-1">{t.pick}</label>
            <div className="relative">
              <select
                value={peerSlug}
                onChange={(e) => setPeerSlug(e.target.value)}
                className="w-full appearance-none bg-white border border-slate-300 rounded-lg px-3 py-2 pr-9 font-semibold text-slate-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none cursor-pointer"
                data-testid="comparator-peer-select"
              >
                {peers.map((s) => {
                  const c = getCountryContent(s, lang);
                  return <option key={s} value={s}>{c?.name || s}</option>;
                })}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-3 bg-slate-100 border-b border-slate-200 text-xs font-mono uppercase text-slate-600 tracking-wide">
            <div className="px-4 py-3">—</div>
            <div className="px-4 py-3 border-l border-slate-200">{current.content?.name}</div>
            <div className="px-4 py-3 border-l border-slate-200">{peer.content?.name}</div>
          </div>
          {rows.map((row, i) => (
            <div
              key={row.label}
              className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
              data-testid={`comparator-row-${i}`}
            >
              <div className="px-4 py-3 font-semibold text-slate-800">{row.label}</div>
              <div className="px-4 py-3 text-slate-700 border-l border-slate-200">{row.a}</div>
              <div className="px-4 py-3 text-slate-700 border-l border-slate-200">{row.b}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-sky-50 border border-sky-200 rounded-xl">
          <div className="font-semibold text-sky-900 mb-1">{t.sameFramework}</div>
          <p className="text-sm text-sky-800">{t.sameFrameworkDesc}</p>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={peerHref}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg transition-colors"
            data-testid="comparator-see-full"
          >
            {t.seeFull} — {peer.content?.name}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={currentHref}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-900 font-semibold rounded-lg border border-slate-300 transition-colors"
          >
            {current.content?.name}
          </Link>
        </div>
      </div>
    </section>
  );
}
